<template>
  <div>
    <!-- ============================================================== -->
    <!-- Bread crumb and right sidebar toggle -->
    <!-- ============================================================== -->
    <div class="page-breadcrumb border-bottom">
      <div class="row">
        <div class="col-lg-3 col-md-4 col-xs-12 align-self-center">
          <h5 class="font-medium text-uppercase mb-0">Webflow Sync</h5>
        </div>
        <div class="col-lg-9 col-md-8 col-xs-12 align-self-center">
          <nav aria-label="breadcrumb" class="float-left mt-2 float-md-right">
            <ol class="p-0 mb-0 breadcrumb justify-content-end">
              <li class="breadcrumb-item">
                <a>Home</a>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Webflow Sync
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
    <!-- ============================================================== -->
    <!-- End Bread crumb and right sidebar toggle -->
    <!-- ============================================================== -->
    <!-- ============================================================== -->
    <!-- Container fluid  -->
    <!-- ============================================================== -->
    <div class="page-content container-fluid">
      <!-- ============================================================== -->
      <!-- Start Page Content -->
      <!-- ============================================================== -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-md-8">
                  <h4 class="card-title">Webflow Pages Synchronization</h4>
                  <p class="card-text">
                    Trigger a sync of Webflow pages via AWS EventBridge. This will update the latest content from Webflow to the system.
                  </p>
                  <div v-if="lastTriggerSyncTime" class="alert alert-info">
                    <i class="fa fa-info-circle mr-1" aria-hidden="true"></i>
                    <strong>Last sync triggered:</strong> {{ lastTriggerSyncTime }}
                  </div>
                  
                  <!-- Sync Options -->
                  <div class="form-group mt-3">
                    <div class="custom-control custom-checkbox">
                      <input
                        id="onlyChangesSinceLastSync"
                        v-model="syncOptions.onlyChangesSinceLastSync"
                        type="checkbox"
                        class="custom-control-input"
                      >
                      <label class="custom-control-label" for="onlyChangesSinceLastSync">
                        <strong>Only sync changes since last sync</strong>
                      </label>
                    </div>
                    <small class="form-text text-muted">
                      When enabled, only fetches and processes content that has changed since the last successful sync. 
                      This can significantly reduce sync time and resource usage.
                    </small>
                  </div>
                </div>
                <div class="col-md-4 text-center">
                  <button
                    class="btn btn-primary btn-lg"
                    :disabled="isLoading"
                    @click="triggerWebflowSync"
                  >
                    <i
                      class="fa fa-sync fa-fw"
                      aria-hidden="true"
                      :class="{ 'fa-spin': isLoading }"
                    ></i>
                    {{ isLoading ? 'Syncing...' : 'Sync Webflow' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Help Information -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">
                <i class="fa fa-question-circle text-info" aria-hidden="true"></i> About Webflow Sync
              </h5>
              <p>
                This function triggers a synchronization process that:
              </p>
              <ul>
                <li>Initiates a sync via AWS EventBridge</li>
                <li>Runs the <code>cli/syncPagesFromWebflow/index.js</code> script in the headless service</li>
                <li>Fetches the latest pages from Webflow</li>
                <li>Updates the system with new content</li>
                <li>Ensures content consistency across platforms</li>
              </ul>
              <div class="alert alert-warning">
                <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
                <strong>Note:</strong> The sync process may take a while to complete. You will see a success message when the trigger is sent successfully, <strong>not when the sync is done</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Config from '@/config'
import utils from '@/utils'

export default {
  name: 'WebflowSync',
  data() {
    return {
      isLoading: false,
      lastTriggerSyncTime: null,
      syncOptions: {
        onlyChangesSinceLastSync: false,
      },
    }
  },
  methods: {
    async triggerWebflowSync() {
      if (this.isLoading) return
      
      this.isLoading = true
      
      try {
        await this.$http.post(Config.api.syncWebflowPages, this.syncOptions)
        
        this.lastTriggerSyncTime = utils.isoDateTime(new Date())
        
        const syncType = this.syncOptions.onlyChangesSinceLastSync ? 'incremental' : 'full'
        utils.showToastSuccess(`Webflow ${syncType} sync triggered successfully! The process will run in the background.`)
      } catch (error) {
        const errorMessage = (error.body && error.body.message) || error.bodyText || 'Failed to trigger Webflow sync. Please try again.'
        utils.showToastError(errorMessage)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-lg {
  padding: 12px 24px;
  font-size: 16px;
}

.alert {
  border-radius: 8px;
}

.card-title {
  color: #2c3e50;
  font-weight: 600;
}

.card-text {
  color: #6c757d;
  font-size: 14px;
}

.custom-control-label {
  font-weight: 500;
  color: #495057;
}

.form-text {
  font-size: 13px;
  line-height: 1.4;
}
</style> 